import React, { useEffect, useState } from 'react';

import {
  Box,
  BoxProps,
  Center,
  Loader,
  Table as MantineTable,
  Skeleton,
  Title,
  createStyles,
} from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import KiloAlert from 'components/KiloAlert';
import { Column, useSortBy, useTable } from 'react-table';

import Th from './components/Th';

const useStyles = createStyles((theme, { margin, bgc }: { margin: number; bgc: 'white' | 'gray' }) => ({
  tableContainer: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    position: 'relative',
    width: `calc(100% - 2 * ${margin}px)`,
    whiteSpace: 'nowrap',
    margin: margin,
    backgroundColor: bgc === 'white' ? theme.white : theme.colors.gray[0],
    fontSize: theme.fontSizes.sm,
  },

  stickyHeader: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    boxShadow: `0 1px 0 ${theme.colors.gray[4]}`,
  },

  stickyRightColumn: {
    width: '80px',
    position: 'sticky',
    right: 0,
    zIndex: 1,
  },
}));

export interface TableProps extends BoxProps {
  tableData: any[];
  columns: Column<any>[] | any;
  isSuccess: boolean;
  isLoading: boolean;
  isError: boolean;
  isFetchingNextPage?: boolean;
  hasNextPage?: boolean;
  fetchNextPage?: () => any;
  clickableHeaders?: string[];
  handleHeaderClick?: (data: any) => void;
  ascending?: boolean;
  ordering?: string;
  handleRowClick?: (data: any) => void;
  rowClassName?: (data: any) => string;
  margin?: number;
  bgc?: 'white' | 'gray';
  EmptyState?: JSX.Element;
  renderFooter?: () => React.ReactNode;
  fillHeight?: boolean;
}

const Table = ({
  tableData,
  columns,
  isSuccess,
  isLoading,
  isError,
  isFetchingNextPage = false,
  hasNextPage = false,
  fetchNextPage = () => {},
  clickableHeaders = [],
  handleHeaderClick = () => {},
  ascending,
  ordering,
  handleRowClick = () => {},
  rowClassName,
  margin = 12,
  bgc = 'white',
  EmptyState = <Title order={4}>No results found</Title>,
  renderFooter,
  fillHeight = false,
}: TableProps): JSX.Element => {
  const { classes, theme } = useStyles({ margin, bgc });

  const { ref, height } = useElementSize();
  const [loaderRowNum, setLoaderRowNum] = useState(4);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data: tableData,
    },
    useSortBy,
  );

  const fetchMoreOnBottomReached = React.useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        if (Math.abs(scrollHeight - scrollTop - clientHeight) <= 1 && !isFetchingNextPage && hasNextPage) {
          fetchNextPage();
        }
      }
    },
    [fetchNextPage, isFetchingNextPage, hasNextPage],
  );

  useEffect(() => {
    if (ref.current?.clientHeight) {
      const heightOfContainer = ref.current.clientHeight - 48;
      setLoaderRowNum(Math.trunc(heightOfContainer / 42));
    }
  }, [height, ref]);

  useEffect(() => {
    fetchMoreOnBottomReached(ref.current);
  }, [fetchMoreOnBottomReached, height, ref]);

  return (
    <Box
      id='scrollable-div'
      ref={ref}
      onScroll={e => fetchMoreOnBottomReached(e.currentTarget)}
      className={classes.tableContainer}
      style={fillHeight ? { flex: 1, minHeight: 0 } : undefined}
    >
      <MantineTable {...getTableProps()}>
        <thead className={classes.stickyHeader}>
          {
            // Loop over the header rows
            headerGroups?.map((headerGroup: any, headerGroupIndex: number) => (
              // Apply the header row props
              <tr {...headerGroup.getHeaderGroupProps()} key={headerGroupIndex}>
                {
                  // Loop over the headers in each row
                  headerGroup.headers.map((column: any) => (
                    // Apply the header cell props
                    <Th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      key={column.id}
                      columnId={column.id}
                      isSortable={clickableHeaders.includes(column.id)}
                      ordering={ordering}
                      ascending={ascending}
                      handleHeaderClick={handleHeaderClick}
                      sticky={column.sticky}
                      bgc={bgc}
                    >
                      {
                        // Render the header
                        column.render('Header')
                      }
                    </Th>
                  ))
                }
              </tr>
            ))
          }
        </thead>
        {isSuccess && (
          <tbody {...getTableBodyProps()}>
            {
              // Loop over the table rows
              rows?.map((row: any) => {
                // Prepare the row for display
                prepareRow(row);
                return (
                  // Apply the row props
                  <tr
                    {...row.getRowProps()}
                    key={row.id}
                    onClick={handleRowClick ? () => handleRowClick(row.original) : undefined}
                    className={rowClassName ? rowClassName(row.original) : undefined}
                  >
                    {
                      // Loop over the rows cells
                      row.cells.map((cell: any) => {
                        // Apply the cell props
                        return (
                          <td
                            {...cell.getCellProps()}
                            key={`${row.id}-${cell.column.id}`}
                            style={{
                              minWidth: cell.column.minWidth || 'auto',
                              maxWidth: cell.column.maxWidth || 'auto',
                              position: cell.column.sticky ? 'sticky' : 'relative',
                              right: cell.column.sticky ? `0px` : undefined,
                              background: cell.column.sticky
                                ? bgc === 'white'
                                  ? theme.white
                                  : theme.colors.gray[0]
                                : undefined,
                            }}
                          >
                            {
                              // Render the cell contents
                              cell.render('Cell')
                            }
                          </td>
                        );
                      })
                    }
                  </tr>
                );
              })
            }
            {isFetchingNextPage && (
              <tr>
                <td colSpan={columns.length}>
                  <Center>
                    <Loader />
                  </Center>
                </td>
              </tr>
            )}
          </tbody>
        )}
        {isSuccess && renderFooter && <tfoot>{renderFooter()}</tfoot>}
      </MantineTable>
      {isLoading && (
        <Box pt={16} sx={{ flexGrow: 1 }}>
          {[...Array(loaderRowNum)].map((_, i) => (
            <Skeleton key={`skeleton-${i}`} height={32} mb={16} />
          ))}
        </Box>
      )}
      {isError && (
        <KiloAlert variantStyle='error' title='Error'>
          Something went wrong while fetching data!
        </KiloAlert>
      )}
      {isSuccess && tableData.length === 0 && <Center p='lg'>{EmptyState}</Center>}
    </Box>
  );
};

export default Table;
